/**
 * BootScene — Scène de démarrage Phaser.
 *
 * Séquence :
 *   1. Lit le JWT depuis Zustand (mis en place par LoginPage)
 *   2. Carte à ouvrir : targetZoneSlug, sinon la carte d'arrivée (éditeur), sinon "lobby", sinon la première
 *      carte du tenant ; aucune carte → un admin est envoyé dans l'éditeur
 *   3. GET /world/zones/:slug/config → ZoneConfig
 *   3. Démarre ZoneScene avec les données nécessaires
 *
 * En cas d'échec réseau : affiche l'erreur dans le canvas (debug dev).
 * Ne jamais importer React ici.
 */
import Phaser from 'phaser';
import { useGameStore } from '@campus/core';
import type { ZoneAssetUrls } from '@campus/core';
import { API_BASE, assetUrl } from '../../lib/apiBase.js';
import { setOpenZones } from '../../lib/access.js';
import { readRecentLastPlace } from '../../lib/lastPlace.js';

/** Jeton pour lequel l'arrivée (dernière place ou bureau) a déjà été tentée (une fois par connexion). */
let deskArrivalDoneFor: string | null = null;

export class BootScene extends Phaser.Scene {
  private statusText: Phaser.GameObjects.Text | null = null;

  constructor() {
    super({ key: 'BootScene' });
  }

  create() {
    this.statusText = this.add.text(
      this.cameras.main.centerX,
      this.cameras.main.centerY,
      'Connexion…',
      { fontSize: '18px', color: '#ffffff', align: 'center' },
    ).setOrigin(0.5);

    // Différer d'un frame : le renderer Phaser doit avoir dessiné au moins
    // un frame avant qu'on appelle setText(), sinon context.drawImage = null.
    this.time.delayedCall(1, () => { void this.boot(); });
  }

  private status(msg: string) {
    if (this.statusText) this.statusText.setText(msg);
    console.log('[BootScene]', msg);
  }

  /**
   * Carte à ouvrir. Un tenant tout neuf n'a pas de carte "lobby" (voire
   * aucune carte) : sans ce repli, le jeu restait bloqué sur une erreur et
   * l'admin n'atteignait jamais le bouton Admin pour créer sa première carte.
   * Retourne null si le jeu ne peut pas démarrer (message déjà affiché).
   */
  private async resolveZoneSlug(token: string, tenantId: string, role: unknown): Promise<string | null> {
    const res = await fetch(`${API_BASE}/world/zones`, {
      headers: { Authorization: `Bearer ${token}`, 'X-Tenant-ID': tenantId },
    });
    if (!res.ok) throw new Error(`Liste des cartes HTTP ${res.status}`);
    const zones = await res.json() as Array<{ slug: string; isArrival?: boolean }>;
    // Portails et « Rejoindre » vers une carte désactivée : fermés (lib/access).
    setOpenZones(zones.map((z) => z.slug));

    // Retour dans l'heure : dernière place ; sinon, membre qui a un bureau : son bureau.
    const deskSlug = await this.deskArrival(token, tenantId, role, zones);
    if (deskSlug) return deskSlug;

    // Carte visée (portail, changement de carte), sinon la carte d'arrivée
    // choisie par l'admin, sinon « lobby ».
    const wanted = useGameStore.getState().targetZoneSlug
      || zones.find((z) => z.isArrival)?.slug
      || 'lobby';

    if (zones.some((z) => z.slug === wanted)) return wanted;
    // Carte visée absente ou hors du profil d'accès (la liste est filtrée par
    // l'API) : la carte d'arrivée, sinon la première.
    const fallback = zones.find((z) => z.isArrival) ?? zones[0];
    if (fallback) {
      useGameStore.getState().setTargetZoneSlug(fallback.slug);
      return fallback.slug;
    }

    // Aucune carte : un admin crée la première dans l'éditeur (bouton
    // « + Nouvelle carte »), les autres attendent.
    if (typeof role === 'string' && ['OWNER', 'ADMIN'].includes(role.toUpperCase())) {
      this.status('Aucune carte pour l\'instant - ouverture de l\'éditeur…');
      useGameStore.getState().setAdminPage('map');
    } else {
      this.status('Le campus est en cours de préparation.\nRevenez un peu plus tard.');
    }
    return null;
  }

  /**
   * Arrivée en entrant dans le campus, une fois par connexion (jeton) : la
   * dernière place si on l'a quittée il y a moins d'une heure (lib/lastPlace,
   * carte toujours ouverte à la personne), sinon le bureau personnel. Seulement en
   * entrant dans le campus (carte visée vide = carte d'arrivée), pas après un
   * portail ou un changement de carte. Position = centre du bureau ; s'il est
   * occupé par un meuble, ZoneScene replace l'avatar sur la case libre la plus
   * proche (ensureLocalPlayerOnFreeCell). Sans bureau, ou carte hors du profil
   * d'accès : carte d'arrivée habituelle.
   */
  private async deskArrival(token: string, tenantId: string, role: unknown, zones: Array<{ slug: string }>): Promise<string | null> {
    if (useGameStore.getState().targetZoneSlug || deskArrivalDoneFor === token) return null;
    deskArrivalDoneFor = token;
    if (typeof role !== 'string' || role.toUpperCase() === 'GUEST') return null;
    const last = readRecentLastPlace();
    if (last && zones.some((z) => z.slug === last.zoneSlug)) {
      useGameStore.getState().setLastPlayerPosition(last);
      useGameStore.getState().setTargetZoneSlug(last.zoneSlug);
      return last.zoneSlug;
    }
    try {
      const res = await fetch(`${API_BASE}/world/desks/personal/mine`, {
        headers: { Authorization: `Bearer ${token}`, 'X-Tenant-ID': tenantId },
      });
      if (!res.ok) return null;
      const desk = await res.json() as { mapId: string; tileX?: number; tileY?: number; width?: number; height?: number; outOfArea?: boolean } | null;
      if (!desk || desk.outOfArea || desk.tileX === undefined || desk.tileY === undefined) return null;
      if (!zones.some((z) => z.slug === desk.mapId)) return null;
      const tile = 32;
      useGameStore.getState().setLastPlayerPosition({
        zoneSlug: desk.mapId,
        x: (desk.tileX + (desk.width ?? 3) / 2) * tile,
        y: (desk.tileY + (desk.height ?? 3) / 2) * tile,
      });
      useGameStore.getState().setTargetZoneSlug(desk.mapId);
      return desk.mapId;
    } catch {
      return null; // pas de bureau trouvé : arrivée habituelle
    }
  }

  private async boot(): Promise<void> {
    try {
      // ── 1. JWT depuis Zustand (mis en place par LoginPage) ──
      this.status('Initialisation…');

      // Vérifier que l'utilisateur est encore authentifié (un kick peut
      // avoir changé authState entre le lancement de BootScene et ce point)
      const { jwt: token, authState } = useGameStore.getState();
      if (authState !== 'in-game' || !token) {
        this.status('Session terminée.');
        return;
      }

      // Décoder le payload JWT (base64url, aucune vérification côté client)
      const payload  = decodeJwtPayload(token);
      const tenantId = payload['tid'] as string;
      const userId   = payload['sub'] as string;

      // ── 2. Zone config ────────────────────────────────────
      const ZONE_SLUG = await this.resolveZoneSlug(token, tenantId, payload['role']);
      if (!ZONE_SLUG) return;
      this.status(`Chargement zone "${ZONE_SLUG}"…`);

      const zoneRes = await fetch(`${API_BASE}/world/zones/${ZONE_SLUG}/config`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
        },
      });

      if (!zoneRes.ok) {
        throw new Error(`Zone config HTTP ${zoneRes.status}`);
      }

      const zoneConfig = await zoneRes.json() as {
        id: string; tenantId: string; slug: string; displayName: string;
        tmjPath: string; wamPath: string; portals: unknown[]; maxOccupants: number;
      };

      // ── 3. Asset URLs ─────────────────────────────────────
      this.status('Chargement assets…');

      const assetRes = await fetch(`${API_BASE}/world/zones/${ZONE_SLUG}/assets`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID':  tenantId,
        },
      });

      if (!assetRes.ok) {
        throw new Error(`Zone assets HTTP ${assetRes.status}: ${await assetRes.text()}`);
      }

      const urls = await assetRes.json() as ZoneAssetUrls;

      // urls.tmj/wam sont des chemins relatifs à /assets/tenants/:tenantId/* —
      // il faut les rendre absolus (API_BASE, domaine différent en prod).
      // L'auth Bearer est attachée par ZoneLoader via xhrSettings au chargement Phaser.
      urls.tmj = assetUrl(urls.tmj);
      urls.wam = assetUrl(urls.wam);

      // ── 4. Démarrage ZoneScene ────────────────────────────
      this.status('Démarrage…');

      this.scene.start('ZoneScene', {
        zoneConfig: {
          id:           zoneConfig.id,
          tenantId:     zoneConfig.tenantId,
          slug:         zoneConfig.slug,
          displayName:  zoneConfig.displayName,
          tmjPath:      zoneConfig.tmjPath,
          wamPath:      zoneConfig.wamPath,
          portals:      zoneConfig.portals ?? [],
          maxOccupants: zoneConfig.maxOccupants,
        },
        localPlayerId: userId,
        urls,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this.status(`Erreur :\n${msg}`);
      console.error('[BootScene] Boot failed:', err);
    }
  }
}

// ── Utilitaire ───────────────────────────────────────────────────────────────

function decodeJwtPayload(token: string): Record<string, unknown> {
  const part = token.split('.')[1] ?? '';
  return JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
}
